import * as React from "react"

import { cva } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"
import { useDecorativeSvgs } from "../../hooks/useDecorativeSvgs"
import { Tooltip } from "../Tooltip/Tooltip"

// A placeholder (or an unfocused value) longer than the field ends in an
// ellipsis instead of being clipped mid-glyph, so fixed-width search fields stay
// readable whatever copy they are given.
const inputVariants = cva([
  "h-control w-full rounded-md px-2 py-1.5",
  "text-sm text-ellipsis text-coval-text placeholder:text-coval-text-muted",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "disabled:cursor-default read-only:cursor-default",
  "border border-surface-1",
  "disabled:opacity-50",
])

const inputWrapperVariants = cva([
  "coval-field flex items-center gap-1.5",
  "h-control w-full rounded-md px-2",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "has-[input:disabled]:cursor-default has-[input:read-only]:cursor-default",
  "border border-surface-1",
  "has-[:disabled]:opacity-50",
])

const innerInputVariants = cva([
  "h-full w-full min-w-0 py-1.5 bg-transparent",
  "text-sm text-ellipsis text-coval-text placeholder:text-coval-text-muted",
  "outline-none",
  "[&[type=search]]:[-webkit-appearance:none] [&[type=search]::-webkit-search-cancel-button]:hidden",
])

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  onClear?: () => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      error,
      startAdornment,
      endAdornment,
      onClear,
      onChange,
      value,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(
      props.defaultValue ?? "",
    )
    const innerRef = React.useRef<HTMLInputElement>(null)
    // The field is named by its label, so an icon adornment is decoration.
    // Text adornments (a "#" or "https://" prefix) still read.
    const setStartAdornmentRef = useDecorativeSvgs<HTMLSpanElement>(undefined, {
      inNamedControls: true,
    })
    const setEndAdornmentRef = useDecorativeSvgs<HTMLSpanElement>(undefined, {
      inNamedControls: true,
    })

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const currentValue = value !== undefined ? value : internalValue
    const showClear = !!onClear && !!currentValue && !disabled

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value)
      }
      onChange?.(e)
    }

    const handleClear = () => {
      if (disabled) {
        return
      }
      if (value === undefined) {
        setInternalValue("")
      }
      onClear?.()
    }

    const resolvedEndAdornment = showClear ? (
      <Tooltip content="Clear">
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear"
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          // A 24px hit area meets WCAG 2.5.8 around the glyph. It is set in px
          // because the root font size scales rem steps below 24px, and the
          // negative margin keeps the glyph where the field's padding put it.
          className="-mr-[6px] inline-flex size-[24px] cursor-pointer items-center justify-center rounded-sm disabled:cursor-default"
        >
          <X aria-hidden="true" />
        </button>
      </Tooltip>
    ) : (
      endAdornment
    )

    if (!startAdornment && !resolvedEndAdornment) {
      return (
        <input
          className={cn(
            inputVariants(),
            error &&
              "border-coval-danger-border focus:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface placeholder:text-coval-danger-on-surface/80",
            className,
          )}
          ref={ref}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          {...props}
        />
      )
    }

    return (
      <div
        role="presentation"
        className={cn(
          inputWrapperVariants(),
          error &&
            "border-coval-danger-border focus-within:border-coval-danger-border bg-coval-danger-surface",
          className,
        )}
        onClick={(event) => {
          // Don't steal focus from nested interactive adornments (the clear
          // button, menu triggers, etc.); only focus on bare-container clicks.
          const target = event.target as HTMLElement
          if (
            target.closest(
              "button,a,input,select,textarea,[role='button'],[role='menuitem']",
            )
          ) {
            return
          }
          innerRef.current?.focus()
        }}
      >
        {startAdornment && (
          <span
            ref={setStartAdornmentRef}
            className={cn(
              "flex shrink-0 items-center text-coval-text-muted [&>svg]:size-4",
              error && "text-coval-danger-on-surface",
            )}
          >
            {startAdornment}
          </span>
        )}
        <input
          className={cn(
            innerInputVariants(),
            error &&
              "text-coval-danger-on-surface placeholder:text-coval-danger-on-surface/80",
          )}
          ref={setRefs}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          {...props}
        />
        {resolvedEndAdornment && (
          <span
            ref={setEndAdornmentRef}
            className={cn(
              "flex shrink-0 items-center text-coval-text-muted [&_svg]:size-3.5",
              error && "text-coval-danger-on-surface",
            )}
          >
            {resolvedEndAdornment}
          </span>
        )}
      </div>
    )
  },
)

Input.displayName = "Input"

export { Input, inputVariants }
